// client/src/api/board/boardApi.ts
//
// Dominio: Board (Lavagna/Bacheca) + Plate Lookup
// Lift-and-shift FEDELE da services/apiClient.ts — nessuna logica riscritta,
// nessun contratto API modificato. Le firme e i return type ricalcano 1:1
// l'originale per garantire retrocompatibilità totale con la UI esistente
// (Board.tsx, Navbar.tsx, Profile.tsx).
//
// Dipende da: @/api/core/boundApiFetch (wrapper con store iniettato)
//             @/api/events (per getEventNotificationsUnreadCount).

import { boundApiFetch } from '@/api/core/boundApiFetch';
import type { BoardNotification } from '@/types';
import { getEventNotificationsUnreadCount } from '@/api/events/eventsApi';

// ── Posts ─────────────────────────────────────────────────────────────────────

export async function getBoardPosts(
  limit = 30,
  opts?: {
    scope?: 'all' | 'public' | 'chapter';
    chapter_id?: string | null;
  },
) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (opts?.scope) qs.set('scope', opts.scope);
  if (opts?.chapter_id) qs.set('chapter_id', opts.chapter_id);
  return boundApiFetch(`/api/board/posts?${qs.toString()}`) as Promise<{
    posts: any[];
  }>;
}

export async function getBoardPost(id: string) {
  return boundApiFetch(`/api/board/posts/${id}`) as Promise<{
    post: any;
    comments: any[];
  }>;
}

export async function createBoardPost(payload: {
  title: string;
  body: string;
  image_url?: string | null;
  visibility?: 'public' | 'chapter';
  chapter_id?: string | null;
}) {
  return boundApiFetch('/api/board/posts', {
    method: 'POST',
    body: JSON.stringify(payload),
  }) as Promise<{ post: any }>;
}

export async function deleteBoardPost(id: string) {
  return boundApiFetch(`/api/board/posts/${id}`, {
    method: 'DELETE',
  }) as Promise<{ ok: true }>;
}

// ── Comments ──────────────────────────────────────────────────────────────────

export async function createBoardComment(
  postId: string,
  payload: { body: string; parent_comment_id?: string | null },
) {
  return boundApiFetch(`/api/board/posts/${postId}/comments`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }) as Promise<{ comment: any }>;
}

// ── Likes ─────────────────────────────────────────────────────────────────────

export async function toggleBoardLike(postId: string) {
  return boundApiFetch(`/api/board/posts/${postId}/like`, {
    method: 'POST',
  }) as Promise<{ likes_count: number; liked_by_me: boolean }>;
}

export async function toggleBoardCommentLike(commentId: string) {
  return boundApiFetch(`/api/board/comments/${commentId}/like`, {
    method: 'POST',
  }) as Promise<{
    comment_id: string;
    likes_count: number;
    liked_by_me: boolean;
  }>;
}

// ── Notifications ─────────────────────────────────────────────────────────────

export async function getBoardNotifications(limit = 30, unreadOnly = false) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (unreadOnly) qs.set('unread_only', '1');
  return boundApiFetch(`/api/board/notifications?${qs.toString()}`) as Promise<{
    unread: number;
    notifications: BoardNotification[];
  }>;
}

export async function getBoardUnreadCount() {
  return boundApiFetch(
    '/api/board/notifications/unread-count',
  ) as Promise<{ unread: number }>;
}

export async function markBoardNotificationsRead(ids: string[]) {
  return boundApiFetch('/api/board/notifications/mark-read', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  }) as Promise<{ ok: true; unread: number }>;
}

// ✅ FIX #API-3: aggrega board + eventi in un'unica chiamata.
// Mantiene il contratto identico al vecchio apiClient.ts.
export async function getTotalUnreadCount(): Promise<{ unread: number }> {
  const [board, events] = await Promise.allSettled([
    getBoardUnreadCount(),
    getEventNotificationsUnreadCount(),
  ]);
  const b = board.status === 'fulfilled' ? board.value.unread : 0;
  const e = events.status === 'fulfilled' ? events.value.unread : 0;
  return { unread: b + e };
}

// ── Plate Lookup (board utils) ────────────────────────────────────────────────

export async function lookupPlate(plate: string) {
  return boundApiFetch('/api/vehicle/plate-lookup', {
    method: 'POST',
    body: JSON.stringify({ plate }),
  }) as Promise<{
    ok: true;
    vehicle: {
      brand?: string | null;
      model?: string | null;
      displacement_cc?: number | null;
      year?: number | null;
      bike_type?: string | null;
    };
  }>;
}
